import { useCallback, useMemo, useRef, useState } from 'react'
import {
  CREDIT_CARD_MAX_LENGTHS,
  formatCreditCardExpiry,
  formatCreditCardNumber,
  shouldAdvanceCreditCardField,
} from './creditCardFormat'
import type {
  CreditCardFieldName,
  CreditCardFormData,
  CreditCardFormErrors,
  CreditCardFormMessages,
  CreditCardType,
} from './creditCardTypes'
import {
  detectCreditCardType,
  digitsOnly,
  validateCreditCardCvv,
  validateCreditCardExpiry,
  validateCreditCardNumber,
  validateCreditCardZip,
} from './creditCardValidation'

interface CreditCardValues {
  cardNumber: string
  expiry: string
  cvv: string
  zip: string
  cardType: CreditCardType | null
}

const EMPTY_ERRORS: CreditCardFormErrors = {
  cardNumber: null,
  expiry: null,
  cvv: null,
  zip: null,
}

function emptyValues(): CreditCardValues {
  return {
    cardNumber: '',
    expiry: '',
    cvv: '',
    zip: '',
    cardType: null,
  }
}

export function useCreditCardForm(
  messages: CreditCardFormMessages,
  supportedCardTypes: readonly CreditCardType[],
  includeZip: boolean,
) {
  const allowedTypes = useMemo<ReadonlySet<CreditCardType> | null>(
    () =>
      supportedCardTypes.length > 0 ? new Set(supportedCardTypes) : null,
    [supportedCardTypes],
  )
  const [values, setValues] = useState<CreditCardValues>(emptyValues)
  const [errors, setErrors] = useState<CreditCardFormErrors>(EMPTY_ERRORS)
  const latestValuesRef = useRef<CreditCardValues>(values)
  const autoAdvancingFromRef = useRef<CreditCardFieldName | null>(null)
  const cardNumberRef = useRef<HTMLInputElement>(null)
  const expiryRef = useRef<HTMLInputElement>(null)
  const cvvRef = useRef<HTMLInputElement>(null)
  const zipRef = useRef<HTMLInputElement>(null)

  const updateValues = useCallback((next: CreditCardValues) => {
    latestValuesRef.current = next
    setValues(next)
  }, [])

  const setCardNumber = useCallback(
    (value: string) => {
      const cardNumber = formatCreditCardNumber(value)
      const cardType = detectCreditCardType(cardNumber)
      const next = { ...latestValuesRef.current, cardNumber, cardType }
      updateValues(next)
      setErrors((current) => ({ ...current, cardNumber: null }))
      if (
        shouldAdvanceCreditCardField('cardNumber', cardNumber, cardType) &&
        validateCreditCardNumber(cardNumber, allowedTypes, messages) === null
      ) {
        autoAdvancingFromRef.current = 'cardNumber'
        expiryRef.current?.focus()
      }
    },
    [allowedTypes, messages, updateValues],
  )

  const setExpiry = useCallback(
    (value: string) => {
      const currentExpiry = latestValuesRef.current.expiry
      const deletingSlash =
        value.length < currentExpiry.length &&
        currentExpiry.includes('/') &&
        !value.includes('/')
      const source = deletingSlash ? digitsOnly(value).slice(0, -1) : value
      const expiry = formatCreditCardExpiry(source)
      const next = { ...latestValuesRef.current, expiry }
      updateValues(next)
      setErrors((current) => ({ ...current, expiry: null }))
      if (
        shouldAdvanceCreditCardField('expiry', expiry, next.cardType) &&
        validateCreditCardExpiry(expiry, messages) === null
      ) {
        autoAdvancingFromRef.current = 'expiry'
        cvvRef.current?.focus()
      }
    },
    [messages, updateValues],
  )

  const setCvv = useCallback(
    (value: string) => {
      const maxLength =
        latestValuesRef.current.cardType === 'amex'
          ? CREDIT_CARD_MAX_LENGTHS.cvvAmex
          : CREDIT_CARD_MAX_LENGTHS.cvv
      const cvv = digitsOnly(value).slice(0, maxLength)
      const next = { ...latestValuesRef.current, cvv }
      updateValues(next)
      setErrors((current) => ({ ...current, cvv: null }))
      if (
        includeZip &&
        shouldAdvanceCreditCardField('cvv', cvv, next.cardType)
      ) {
        autoAdvancingFromRef.current = 'cvv'
        zipRef.current?.focus()
      }
    },
    [includeZip, updateValues],
  )

  const setZip = useCallback(
    (value: string) => {
      const zip = digitsOnly(value).slice(0, CREDIT_CARD_MAX_LENGTHS.zip)
      updateValues({ ...latestValuesRef.current, zip })
      setErrors((current) => ({ ...current, zip: null }))
    },
    [updateValues],
  )

  const validateField = useCallback(
    (field: CreditCardFieldName): string | null => {
      const current = latestValuesRef.current
      if (field === 'cardNumber') {
        return validateCreditCardNumber(
          current.cardNumber,
          allowedTypes,
          messages,
        )
      }
      if (field === 'expiry') {
        return validateCreditCardExpiry(current.expiry, messages)
      }
      if (field === 'cvv') {
        return validateCreditCardCvv(current.cvv, current.cardType, messages)
      }
      return includeZip ? validateCreditCardZip(current.zip, messages) : null
    },
    [allowedTypes, includeZip, messages],
  )

  const onBlur = useCallback(
    (field: CreditCardFieldName) => {
      if (autoAdvancingFromRef.current === field) {
        autoAdvancingFromRef.current = null
        return
      }
      setErrors((current) => ({
        ...current,
        [field]: validateField(field),
      }))
    },
    [validateField],
  )

  const onFocus = useCallback((field: CreditCardFieldName) => {
    setErrors((current) => ({ ...current, [field]: null }))
  }, [])

  const validateAll = useCallback((): boolean => {
    const nextErrors: CreditCardFormErrors = {
      cardNumber: validateField('cardNumber'),
      expiry: validateField('expiry'),
      cvv: validateField('cvv'),
      zip: validateField('zip'),
    }
    setErrors(nextErrors)
    return Object.values(nextErrors).every((error) => error === null)
  }, [validateField])

  const getFormData = useCallback((): CreditCardFormData => {
    const current = latestValuesRef.current
    return {
      cardNumber: digitsOnly(current.cardNumber),
      expiry: digitsOnly(current.expiry),
      cvv: current.cvv,
      zip: current.zip,
      cardType: current.cardType,
    }
  }, [])

  const reset = useCallback(() => {
    const next = emptyValues()
    latestValuesRef.current = next
    setValues(next)
    setErrors(EMPTY_ERRORS)
  }, [])

  return {
    values,
    errors,
    refs: { cardNumberRef, expiryRef, cvvRef, zipRef },
    actions: {
      setCardNumber,
      setExpiry,
      setCvv,
      setZip,
      onBlur,
      onFocus,
      validateAll,
      getFormData,
      reset,
    },
  }
}
